import { create } from 'zustand'
import { FileItem } from '../../types'
import { supabase } from '../lib/supabase'

interface FileManagementState {
  files: FileItem[]
  loading: boolean
  error: string | null
  uploadProgress: number
}

interface FileManagementActions {
  fetchFiles: () => Promise<void>
  uploadFile: (file: File, category: string, description?: string) => Promise<{ error?: string }>
  deleteFile: (fileId: string) => Promise<{ error?: string }>
  downloadFile: (fileId: string) => Promise<{ error?: string; url?: string }>
  clearError: () => void
}

type FileManagementStore = FileManagementState & FileManagementActions

// The four file functions are admin-gated server-side, so every call has to
// carry the Supabase access token.
async function authHeader(): Promise<Record<string, string>> {
  const { data } = await supabase.auth.getSession()
  const token = data?.session?.access_token
  if (!token) throw new Error('Nicht angemeldet')
  return { Authorization: `Bearer ${token}` }
}

export const useFileManagementStore = create<FileManagementStore>((set, get) => ({
  files: [],
  loading: false,
  error: null,
  uploadProgress: 0,

  fetchFiles: async () => {
    set({ loading: true, error: null })

    try {
      const response = await fetch('/.netlify/functions/get-files', {
        headers: await authHeader(),
      })

      if (!response.ok) {
        throw new Error('Failed to fetch files')
      }

      const files = await response.json()
      set({ files, loading: false })
    } catch (error: any) {
      console.error('Error fetching files:', error)
      set({ error: error.message, loading: false })
    }
  },

  uploadFile: async (file: File, category: string, description?: string) => {
    set({ loading: true, error: null, uploadProgress: 0 })

    try {
      // Create form data
      const formData = new FormData()
      formData.append('file', file)
      formData.append('category', category)
      formData.append('description', description || '')

      const response = await fetch('/.netlify/functions/upload-file', {
        method: 'POST',
        headers: await authHeader(),
        body: formData
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Upload failed')
      }

      const uploadedFile = await response.json()

      // Update local state
      set(state => ({
        files: [uploadedFile, ...state.files],
        loading: false,
        uploadProgress: 100
      }))

      return { error: undefined }
    } catch (error: any) {
      console.error('Error uploading file:', error)
      set({ error: error.message, loading: false, uploadProgress: 0 })
      return { error: error.message }
    }
  },

  deleteFile: async (fileId: string) => {
    set({ loading: true, error: null })

    try {
      // Find the file to get its key
      const file = get().files.find(f => f.id === fileId)
      if (!file) {
        throw new Error('File not found')
      }

      // Use the file name as the key (we'll need to reconstruct the full key)
      const response = await fetch(`/.netlify/functions/delete-file?key=${encodeURIComponent(file.category + '/' + file.name)}`, {
        method: 'DELETE',
        headers: await authHeader()
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Delete failed')
      }

      // Update local state
      set(state => ({
        files: state.files.filter(f => f.id !== fileId),
        loading: false
      }))

      return { error: undefined }
    } catch (error: any) {
      console.error('Error deleting file:', error)
      set({ error: error.message, loading: false })
      return { error: error.message }
    }
  },

  downloadFile: async (fileId: string) => {
    try {
      const file = get().files.find(f => f.id === fileId)
      if (!file) throw new Error('File not found')

      // download-file now requires an Authorization header, which a plain
      // <a href> cannot send - so fetch the bytes and hand the browser a
      // short-lived blob URL instead.
      const fileKey = `${file.category}/${file.name}`
      const response = await fetch(`/.netlify/functions/download-file?key=${encodeURIComponent(fileKey)}`, {
        headers: await authHeader()
      })

      if (!response.ok) {
        throw new Error('Download failed')
      }

      const blob = await response.blob()
      const objectUrl = URL.createObjectURL(blob)

      const link = document.createElement('a')
      link.href = objectUrl
      link.download = file.originalName
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(objectUrl)

      return { error: undefined }
    } catch (error: any) {
      console.error('Error downloading file:', error)
      return { error: error.message }
    }
  },

  clearError: () => set({ error: null })
}))
